import { Popover } from "@base-ui/react/popover";
import { CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { invalidDataTriggerControlStyles } from "@/components/ui/control-styles";
import { Input } from "@/components/ui/input";
import { useTimezone } from "@/lib/timezone";
import { cn } from "@/lib/utils";

export function dateParts(value: string, timezone: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    }).formatToParts(date);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value || 0);
    const hour = get("hour") % 24;
    const minute = get("minute");
    return {
      year: get("year"),
      month: get("month") - 1,
      day: get("day"),
      time: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
    };
  } catch {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth(),
      day: date.getUTCDate(),
      time: `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`,
    };
  }
}

export function localToUtcIso(
  year: number,
  month: number,
  day: number,
  hours: number,
  minutes: number,
  timezone: string,
): string {
  if (timezone === "UTC") {
    return new Date(Date.UTC(year, month, day, hours, minutes)).toISOString();
  }
  const targetWallClock = Date.UTC(year, month, day, hours, minutes);
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    });
    const wallClockAt = (instant: number) => {
      const parts = formatter.formatToParts(new Date(instant));
      const get = (type: string) => Number(parts.find((p) => p.type === type)?.value || 0);
      return Date.UTC(
        get("year"),
        get("month") - 1,
        get("day"),
        get("hour") % 24,
        get("minute"),
        get("second"),
      );
    };
    let candidate = targetWallClock;
    let previousCandidate: number | undefined;
    for (let attempt = 0; attempt < 4; attempt += 1) {
      const observedWallClock = wallClockAt(candidate);
      const correction = targetWallClock - observedWallClock;
      if (correction === 0) {
        // Repeated wall-clock times occur twice when DST ends. Use the offset
        // from the previous day to detect and prefer the first occurrence.
        const previousDay = candidate - 24 * 60 * 60 * 1000;
        const previousOffset = wallClockAt(previousDay) - previousDay;
        const earlierCandidate = targetWallClock - previousOffset;
        if (earlierCandidate < candidate && wallClockAt(earlierCandidate) === targetWallClock) {
          return new Date(earlierCandidate).toISOString();
        }
        return new Date(candidate).toISOString();
      }

      const nextCandidate = candidate + correction;
      if (nextCandidate === previousCandidate) {
        // A DST gap has no exact instant. Match Date's compatible behavior by
        // choosing the later side of the gap so the wall clock moves forward.
        return new Date(Math.max(candidate, nextCandidate)).toISOString();
      }
      previousCandidate = candidate;
      candidate = nextCandidate;
    }
    return new Date(candidate).toISOString();
  } catch {
    return new Date(targetWallClock).toISOString();
  }
}

function formatDisplayDateTime(value: string, placeholder: string, timezone: string) {
  const parts = dateParts(value, timezone);
  if (!parts) return placeholder;
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: timezone,
      timeZoneName: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export function DateTimePicker({
  id,
  value,
  onValueChange,
  disabled,
  invalid,
  placeholder = "Set date and time",
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
}: {
  id: string;
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
}) {
  const { t } = useTranslation();
  const { resolvedTimezone, timezoneAbbr } = useTimezone();
  const parts = dateParts(value, resolvedTimezone);
  const selected = parts ? new Date(parts.year, parts.month, parts.day) : undefined;
  const today = () => {
    const todayParts = dateParts(new Date().toISOString(), resolvedTimezone);
    return todayParts
      ? new Date(todayParts.year, todayParts.month, todayParts.day)
      : new Date();
  };

  const commit = (next: Date, time = parts?.time ?? "23:59") => {
    const [hours, minutes] = time.split(":").map(Number);
    const iso = localToUtcIso(
      next.getFullYear(),
      next.getMonth(),
      next.getDate(),
      hours || 0,
      minutes || 0,
      resolvedTimezone,
    );
    onValueChange(iso);
  };

  return (
    <Popover.Root>
      <Popover.Trigger
        id={id}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        data-invalid={invalid || undefined}
        disabled={disabled}
        render={
          <Button
            type="button"
            variant="outline"
            className={cn(
              invalidDataTriggerControlStyles,
              "w-full justify-between rounded-sm px-3.5 font-normal hover:bg-canvas active:scale-100 disabled:opacity-100",
            )}
          />
        }
      >
        <span className={cn(!value && "text-muted")}>
          {formatDisplayDateTime(value, placeholder, resolvedTimezone)}
        </span>
        <CalendarDays className="size-4 text-muted" aria-hidden="true" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner
          side="bottom"
          align="end"
          sideOffset={6}
          collisionPadding={12}
          collisionAvoidance={{ side: "flip", align: "shift", fallbackAxisSide: "none" }}
          className="z-50"
        >
          <Popover.Popup className="w-[min(17.5rem,calc(100vw-2rem))] max-h-(--available-height) overflow-y-auto console-scrollbar rounded-md border border-line bg-panel-raised p-3 shadow-panel outline-hidden transition-all duration-150 data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 motion-reduce:transform-none motion-reduce:transition-none pointer-coarse:w-[min(20rem,calc(100vw-0.5rem))]">
            <div className="mb-2.5 flex items-end gap-2 border-b border-line pb-2.5">
              <label className="min-w-0 flex-1 text-xs font-medium text-ink">
                Time ({timezoneAbbr})
                <Input
                  className="mt-1 h-8 text-xs"
                  type="time"
                  value={parts?.time ?? "23:59"}
                  onChange={(event) =>
                    commit(selected ?? today(), event.target.value)
                  }
                />
              </label>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={() => onValueChange("")}
                >
                  {t("common.clear", "Clear")}
                </Button>
                <Popover.Close
                  render={
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs"
                    />
                  }
                >
                  {t("common.close", "Close")}
                </Popover.Close>
              </div>
            </div>
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(next) => next && commit(next)}
              fixedWeeks
              className="w-full"
              showOutsideDays
            />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
